import { useParams } from "@tanstack/react-router";

import { useProject, useViewer } from "../api/hooks";
import type { Org } from "@magicweave/api";

/**
 * Which organisation a screen is talking about.
 *
 * Its own module rather than a corner of allowance.ts, which is where it lived
 * when the allowance chip was the only thing that asked. Four places now answer
 * this question and three of them used to answer it differently — by reading
 * `viewer.orgs[0]` — which is correct exactly until somebody belongs to two
 * studios, and then silently wrong: their second studio's projects stop being
 * listed anywhere and nothing links to them.
 *
 * Three sources, in this order, and the order is the point. An org-level screen
 * names it in the URL, so Plan and Team answer for the organisation being read
 * rather than for a default. A project screen has no `orgId` and does not need
 * one — the project record carries it, and it has already been fetched for the
 * switcher in the strip. Everything else — the account screen, the first-run
 * screen — falls back to the first organisation the viewer belongs to, which is
 * the only answer available when nothing on screen names one.
 */
export function useCurrentOrg(): string | undefined {
  const { orgId, projectId } = useParams({ strict: false });
  const project = useProject(projectId ?? "");
  const viewer = useViewer();
  return resolveOrg(orgId, project.data?.org_id, viewer.data?.orgs);
}

/**
 * The resolution itself, apart from the hooks that feed it.
 *
 * Pure so that it can be tested. The bug this replaced — three screens reading
 * `orgs[0]` while a fourth resolved properly — was invisible in every test
 * because none of them could reach the question: it lived inside a hook, in a
 * component, behind two queries.
 */
export function resolveOrg(
  fromUrl: string | undefined,
  fromProject: string | undefined,
  memberships: readonly Org[] | undefined,
): string | undefined {
  return fromUrl ?? fromProject ?? memberships?.[0]?.id;
}

/**
 * The current organisation as the viewer sees it — name, slug, role.
 *
 * From the viewer rather than its own request. `/v1/me` already returns every
 * organisation a person belongs to, so a second call to fetch the name of one
 * of them is a request for something already in the cache.
 */
export function useCurrentOrgSummary(): Org | undefined {
  const id = useCurrentOrg();
  const viewer = useViewer();
  if (!id) return undefined;
  return viewer.data?.orgs.find((o) => o.id === id);
}

/**
 * Every organisation this person belongs to.
 *
 * Usually one. It is more than one only by invitation — a contractor at two
 * studios — because creating a second is refused by the server: one person, one
 * studio, or their billing, their team and their plan end up split across two
 * places with no way to move a game between them.
 *
 * So the switcher this feeds is not a feature anybody asked for. It exists
 * because a membership somebody else granted must not leave a studio
 * unreachable, and it disappears for the overwhelming majority who have one.
 */
export function useOrgs(): Org[] {
  return useViewer().data?.orgs ?? [];
}
